import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  useOpenLoopChatIntegration,
  useIsOpenLoopChatEnabled,
  useOpenLoopChatPreviewEnabled,
  usePortalTheming,
} from "../../shared/store/portalSchemaStore";
import { useEnduserIsAuthenticated, useEnduserCurrentEnduser } from "../store/hooks";
import { useOrganization } from "../../shared/store/organizationStore";
import { OPENLOOP_CHAT_VALIDATION_PATTERNS } from "../../shared/schema";
import { formatName, getContrastingForegroundHex } from "@tellescope/utils";

/**
 * Validates all OpenLoop Chat configuration values against their regex patterns.
 * This provides runtime validation as a defense-in-depth measure.
 */
function validateConfig(config: {
  source?: string;
  snippetId?: string;
  sessionId?: string;
  platformName?: string;
}): boolean {
  if (!config.source || !OPENLOOP_CHAT_VALIDATION_PATTERNS.source.test(config.source)) {
    console.warn("OpenLoopChatWidget: Invalid source URL");
    return false;
  }
  if (!config.snippetId || !OPENLOOP_CHAT_VALIDATION_PATTERNS.snippetId.test(config.snippetId)) {
    console.warn("OpenLoopChatWidget: Invalid snippet ID");
    return false;
  }
  if (!config.sessionId || !OPENLOOP_CHAT_VALIDATION_PATTERNS.sessionId.test(config.sessionId)) {
    console.warn("OpenLoopChatWidget: Invalid session ID");
    return false;
  }
  if (!config.platformName || !OPENLOOP_CHAT_VALIDATION_PATTERNS.platformName.test(config.platformName)) {
    console.warn("OpenLoopChatWidget: Invalid platform name");
    return false;
  }
  return true;
}

/**
 * OpenLoopChatWidget injects the Amazon Connect chat widget script
 * when the OpenLoop Chat integration is enabled in portal settings.
 *
 * This component renders nothing visible - it only manages script injection.
 * The widget only appears for authenticated endusers (not on login, register,
 * landing, or other unauthenticated pages). Admin preview mode bypasses this.
 */
export const OpenLoopChatWidget: React.FC = () => {
  const { t } = useTranslation("portal");
  const location = useLocation();
  const isEnabled = useIsOpenLoopChatEnabled();
  const config = useOpenLoopChatIntegration();
  const isPreviewEnabled = useOpenLoopChatPreviewEnabled();
  const isAuthenticated = useEnduserIsAuthenticated();
  const currentEnduser = useEnduserCurrentEnduser();
  const organization = useOrganization();
  const theming = usePortalTheming();

  // Derive widget colors from theme with fallbacks to OpenLoop defaults
  const widgetPrimaryColor = theming?.primaryColor ?? "#e90a54";
  // Only calculate contrast when org has custom theme; preserve original OpenLoop light text for default
  const widgetForegroundColor = theming?.primaryColor
    ? getContrastingForegroundHex(widgetPrimaryColor) ?? "#fbfaff"
    : "#fbfaff";

  // Derive branding from config with fallbacks to organization defaults
  const headerMessage =
    config?.headerMessage || t("Welcome to {{name}}!", { name: organization?.name || t("our portal") });
  const logoUrl = config?.logoUrl || "";
  const logoAltText = config?.logoAltText || organization?.name || "";

  // Don't show on admin pages unless preview is enabled
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Derive user contact attributes for Amazon Connect
  const fullName = currentEnduser ? formatName(currentEnduser) || undefined : undefined;
  const userEmail = currentEnduser?.email;
  const userPhone = currentEnduser?.phone;
  const platformName = config?.platformName || organization?.name;

  useEffect(() => {
    // Skip on admin routes unless preview mode is enabled
    if (isAdminRoute && !isPreviewEnabled) {
      return;
    }

    // Only show for authenticated endusers (skip login, register, landing pages)
    // Admin preview mode bypasses this check
    if (!isAdminRoute && !isAuthenticated && !isPreviewEnabled) {
      return;
    }

    // Only inject if enabled
    if (!isEnabled) {
      return;
    }

    // Validate all configuration values against regex patterns before injecting
    if (!config || !validateConfig(config)) {
      return;
    }

    // Check if script already exists
    if (document.getElementById(`openloop-chat-${config.sessionId}`)) {
      return;
    }

    // Build contact attributes object with user data when available
    const contactAttributes: Record<string, string> = {
      PlatformName: platformName || "",
    };
    if (fullName) contactAttributes.Name = fullName;
    if (userPhone) contactAttributes.Phone = userPhone;
    if (userEmail) contactAttributes.Email = userEmail;

    // Create and inject the script
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = `openloop-chat-${config.sessionId}`;

    // Use JSON.stringify for safe value injection to prevent XSS
    script.textContent = `
      (function (w, d, x, id) {
        s = d.createElement('script');
        s.src = ${JSON.stringify(config.source)};
        s.async = 1;
        s.id = id;
        d.getElementsByTagName('head')[0].appendChild(s);
        w[x] = w[x] || function () { (w[x].ac = w[x].ac || []).push(arguments) };
      })(window, document, 'amazon_connect', ${JSON.stringify(config.sessionId)});

      amazon_connect('styles', { iconType: 'CHAT', openChat: { color: ${JSON.stringify(
        widgetForegroundColor
      )}, backgroundColor: ${JSON.stringify(widgetPrimaryColor)} }, closeChat: { color: ${JSON.stringify(
        widgetForegroundColor
      )}, backgroundColor: ${JSON.stringify(widgetPrimaryColor)} } });
      amazon_connect('snippetId', ${JSON.stringify(config.snippetId)});
      amazon_connect('supportedMessagingContentTypes', ['text/plain', 'text/markdown', 'application/vnd.amazonaws.connect.message.interactive', 'application/vnd.amazonaws.connect.message.interactive.response']);
      amazon_connect('customDisplayNames', {
        header: {
          headerMessage: ${JSON.stringify(headerMessage)},
          logoUrl: ${JSON.stringify(logoUrl)},
          logoAltText: ${JSON.stringify(logoAltText)},
        },
        transcript: {
          systemMessageDisplayName: "Agent",
          botMessageDisplayName: "Agent"
        },
        footer: {
          textInputPlaceholder: "Type Here!",
          endChatButtonText: "End Session",
          closeChatButtonText: "Close Chat",
          startCallButtonText: "Start Call"
        },
      });
      amazon_connect('customizationObject', {
        header: {
          dropdown: true,
          dynamicHeader: false,
          hideTranscriptDownloadButton: true
        },
        transcript: {
          hideDisplayNames: false,
          eventNames: {
            customer: ${JSON.stringify(fullName || "You")},
            agent: "Webchat Agent",
            bot: "Agent",
            system: "Agent",
            supervisor: "Webchat Supervisor"
          },
          displayIcons: true,
          iconSources: {
            botMessage: "https://png.pngtree.com/png-clipart/20230820/original/pngtree-chatbot-icon-chat-bot-robot-picture-image_8080841.png",
            systemMessage: "https://png.pngtree.com/png-clipart/20230820/original/pngtree-chatbot-icon-chat-bot-robot-picture-image_8080841.png",
            agentMessage: "https://png.pngtree.com/png-clipart/20230820/original/pngtree-chatbot-icon-chat-bot-robot-picture-image_8080841.png",
          }
        }
      });
      amazon_connect('customStyles', {
        global: {
          frameWidth: '500px',
          frameHeight: '700px',
          textColor: '#fe3251',
          fontSize: '20px',
          footerHeight: '120px',
          typeface: "'AmazonEmber-Light', serif",
          customTypefaceStylesheetUrl: "https://ds6yc8t7pnx74.cloudfront.net/etc.clientlibs/developer-portal/clientlibs/main/css/resources/fonts/AmazonEmber_Lt.ttf",
          headerHeight: '120px',
        },
        header: {
          headerTextColor: ${JSON.stringify(widgetForegroundColor)},
          headerBackgroundColor: ${JSON.stringify(widgetPrimaryColor)},
        },
        transcript: {
          messageFontSize: '13px',
          messageTextColor: '#1f1e1f',
          widgetBackgroundColor: '#f6f4ff',
          agentMessageTextColor: '#1f1e1f',
          systemMessageTextColor: '#1f1e1f',
          customerMessageTextColor: '#1f1e1f',
          agentChatBubbleColor: '#e9e9e9',
          systemChatBubbleColor: '#e9e9e9',
          customerChatBubbleColor: '#f9f2f9',
        },
        footer: {
          buttonFontSize: '20px',
          buttonTextColor: ${JSON.stringify(widgetForegroundColor)},
          buttonBorderColor: ${JSON.stringify(widgetPrimaryColor)},
          buttonholer: ${JSON.stringify(widgetPrimaryColor)},
          buttonBackgroundColor: ${JSON.stringify(widgetPrimaryColor)},
          backgroundColor: ${JSON.stringify(widgetPrimaryColor)},
          footerBackgroundColor: '#0e80f2',
          startCallButtonTextColor: '#541218',
          startChatButtonBorderColor: '#fe3',
          startCallButtonBackgroundColor: '#fe3',
        },
        logo: {
          logoMaxHeight: '61px',
          logoMaxWidth: '99%',
        }
      });
      amazon_connect('contactAttributes', ${JSON.stringify(contactAttributes)});
      ${
        fullName
          ? `amazon_connect('customerDisplayName', function (callback) { callback(${JSON.stringify(fullName)}); });`
          : ""
      }
    `;

    document.head.appendChild(script);

    // Cleanup on unmount or when auth state changes
    return () => {
      // Attempt to gracefully disconnect any active chat session
      const chatIframe = document.getElementById("amazon-connect-chat-widget-iframe");
      if (chatIframe) {
        try {
          const iframeWindow = (chatIframe as HTMLIFrameElement).contentWindow as
            | (Window & { connect?: { ChatSession?: { disconnect?: () => void } } })
            | null;
          iframeWindow?.connect?.ChatSession?.disconnect?.();
        } catch {
          // Silently ignore - iframe may already be cleaned up
        }
      }

      // Remove the wrapper script we created
      const existingScript = document.getElementById(`openloop-chat-${config.sessionId}`);
      if (existingScript) {
        existingScript.remove();
      }

      // Remove the dynamically loaded Amazon Connect script
      const amazonScript = document.querySelector(`script[src="${config.source}"]`);
      if (amazonScript) {
        amazonScript.remove();
      }

      // Remove all Amazon Connect UI elements
      const amazonConnectElementIds = [
        "amazon-connect-open-widget-button",
        "amazon-connect-close-widget-button",
        "amazon-connect-widget-frame",
        "amazon-connect-chat-widget-iframe",
      ];

      for (const elementId of amazonConnectElementIds) {
        document.getElementById(elementId)?.remove();
      }

      // Clean up the amazon_connect global if it exists
      if (typeof window !== "undefined" && "amazon_connect" in window) {
        delete (window as Record<string, unknown>).amazon_connect;
      }

      // Clean up the connect global that Amazon Connect may create
      if (typeof window !== "undefined" && "connect" in window) {
        delete (window as Record<string, unknown>).connect;
      }
    };
  }, [
    isEnabled,
    config,
    isAdminRoute,
    isPreviewEnabled,
    isAuthenticated,
    fullName,
    userEmail,
    userPhone,
    platformName,
    widgetPrimaryColor,
    widgetForegroundColor,
    headerMessage,
    logoUrl,
    logoAltText,
  ]);

  return null; // This component doesn't render anything visible
};
